import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Avatar, IconButton, Tooltip } from '@mui/material';
import { Star, StarBorder, Block, BlockOutlined } from '@mui/icons-material';
import { outletApi } from 'src/api/outletApi';
import { toast } from 'react-hot-toast';

import { useDecodedToken } from 'src/hooks/useDecodedToken';

const OutletDetails = ({ query }) => {
  const { isExpert } = useDecodedToken();

  const [managedOutlets, setManagedOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentOutlet, setCurrentOutlet] = useState(null);

  const toTitleCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        setLoading(true);
        const outlets = await outletApi.fetchOutlets();
        setManagedOutlets(outlets);

        // Find if current outlet exists in managed outlets
        if (query.mediaOutlet) {
          const outletName = query.mediaOutlet.trim();
          const foundOutlet = outlets.find((o) => {
            return o.outlet.toLowerCase() === outletName.toLowerCase();
          });
          setCurrentOutlet(foundOutlet || null);
        }
      } catch (error) {
        toast.error('Error fetching outlets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutlets();
  }, [query.mediaOutlet]);

  const handleFavorite = async () => {
    try {
      if (!query.mediaOutlet) return;

      const outletName = query.mediaOutlet.trim();

      if (currentOutlet) {
        // If already favorited, delete outlet
        if (currentOutlet.type === 'favourited') {
          await outletApi.deleteOutlet(currentOutlet.id);
          setCurrentOutlet(null);
        }
        // If blacklisted, change to favorited
        else if (currentOutlet.type === 'blacklisted') {
          await outletApi.updateOutlet(currentOutlet.id, outletName, 'favourited');
          setCurrentOutlet({ ...currentOutlet, type: 'favourited' });
        }
        // If no type, set to favorited
        else {
          await outletApi.updateOutlet(currentOutlet.id, outletName, 'favourited');
          setCurrentOutlet({ ...currentOutlet, type: 'favourited' });
        }
      } else {
        // Create new outlet with favorited type
        await outletApi.submitOutlet(outletName, 'favourited');
        const newOutlet = { id: Date.now(), outlet: outletName, type: 'favourited' };
        setCurrentOutlet(newOutlet);
      }

      // Refresh outlets list
      const outlets = await outletApi.fetchOutlets();
      setManagedOutlets(outlets);
    } catch (error) {
      toast.error('Error updating outlet:', error);
    }
  };

  const handleBlacklist = async () => {
    try {
      if (!query.mediaOutlet) return;

      const outletName = query.mediaOutlet.trim();

      if (currentOutlet) {
        // If already blacklisted, delete outlet
        if (currentOutlet.type === 'blacklisted') {
          await outletApi.deleteOutlet(currentOutlet.id);
          setCurrentOutlet(null);
        }
        // If favorited, change to blacklisted
        else if (currentOutlet.type === 'favourited') {
          await outletApi.updateOutlet(currentOutlet.id, outletName, 'blacklisted');
          setCurrentOutlet({ ...currentOutlet, type: 'blacklisted' });
        }
        // If no type, set to blacklisted
        else {
          await outletApi.updateOutlet(currentOutlet.id, outletName, 'blacklisted');
          setCurrentOutlet({ ...currentOutlet, type: 'blacklisted' });
        }
      } else {
        // Create new outlet with blacklisted type
        await outletApi.submitOutlet(outletName, 'blacklisted');
        const newOutlet = { id: Date.now(), outlet: outletName, type: 'blacklisted' };
        setCurrentOutlet(newOutlet);
      }

      // Refresh outlets list
      const outlets = await outletApi.fetchOutlets();
      setManagedOutlets(outlets);
    } catch (error) {
      toast.error('Error updating outlet:', error);
    }
  };

  const isFavorited = currentOutlet?.type === 'favourited';
  const isBlacklisted = currentOutlet?.type === 'blacklisted';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
      <Typography
        variant="body1"
        sx={{
          minWidth: { xs: '100%', sm: 150 },
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          mb: { xs: 1, sm: 0 },
        }}
      >
        Outlet:
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
          <Avatar
            src={query.qwotedImage}
            sx={{
              width: { xs: 48, sm: 64 },
              height: { xs: 48, sm: 64 },
              mb: 1,
              '& img': {
                objectFit: 'contain',
                backgroundColor: 'white',
              },
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              flexGrow: 1,
              textAlign: 'left',
            }}
          >
            {toTitleCase(query.mediaOutlet)}
          </Typography>
        </Box>
        {!isExpert && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1, width: '100%' }}>
            <Tooltip title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}>
              <span>
                <IconButton
                  onClick={handleFavorite}
                  color={isFavorited ? 'primary' : 'default'}
                  disabled={loading}
                  sx={{ marginLeft: 0, paddingLeft: 0 }}
                >
                  {isFavorited ? <Star /> : <StarBorder />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isBlacklisted ? 'Remove from blacklist' : 'Add to blacklist'}>
              <span>
                <IconButton onClick={handleBlacklist} color={isBlacklisted ? 'error' : 'default'} disabled={loading}>
                  {isBlacklisted ? <Block /> : <BlockOutlined />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

OutletDetails.propTypes = {
  query: PropTypes.object.isRequired,
};

export default OutletDetails;
